(ns profile.views
  (:require
    [reagent.core :as r]
    [oops.core :refer [oget]]
    [very.re-frame.core :as rf :refer [>evt]]
    [css.core :refer [defstyled] :as css]
    [css.form :as form]
    [css.vars :as vars]
    [css.typography :as typo]
    [cljss.core :as cljss]
    [main.common :as common]
    [priorities.views :as priorities]
    [profile.information :as information]
    [assets-and-liabilities.views :as assets-liabilities]
    [estate-and-tax.views :as estate]
    [expenses.views :as expenses]
    [income.views :as income]
    [insurance.views :as insurance]
    [accounts.common :as modal-common]
    [risk-profile.views :as risk])

  (:require-macros
    [logging.core :refer [log]]))

(defn long-str [& strings]
  (clojure.string/join "" strings))

(defstyled page-title-s :h1
  {:font-size "2.26rem"
   :color vars/title
   :font-weight "300"
   :margin "5rem 0 2rem"})

(defstyled layout-s :div
  {:display "grid"
   :grid-template-columns ".5fr .5fr"
   :grid-gap "2.5rem"
   ::cljss/media {[[:max-width "768px"]]
                  {:grid-template-columns "1fr"
                   :grid-gap "3rem"}}})

(defstyled row-s :div
  {:display "flex"
   :flex-direction "row"
   :align-items "center"})

(defn risk-profile [{:keys [score expenses] :as risk}]
  [:section
   [common/profile-section-header {:title "My risk profile"}]
   (if risk
     [:div
      [risk/risk-profile-summary-text {:score score
                                       :expenses expenses}]
      [common/complete-button {:text "Retake Risk Tolerance Survey"
                               :on-click #(rf/>evt [:profile/edit-risk-survey])}]]
     [common/no-results
      {:text (long-str "Your risk tolerance helps us determine where we should allocate your savings. "
                       "Find out now whether you are a conservative, moderate, or aggressive investor.")
       :button-text "Take Risk Tolerance Survey"
       :button-click #(rf/>evt [:profile/edit-risk-survey])}])])

(defstyled profile-grid-s :div
  {:display "grid"
   :grid-column "content"
   :grid-template-columns "[left-gutter-start] minmax(0, 1fr) [left-gutter-end content-start] minmax(min-content, 82rem) [content-end right-gutter-start] minmax(0, 1fr) [right-gutter-end]"})

(defstyled profile-s :div
  {:grid-column "content"})

(defstyled profile-form-s :div
  {:grid-column "content"})

(defn- profile-grid [& children]
  [profile-grid-s
   (into [profile-form-s] children)])

(defstyled navigation-confirmation-modal-s :div
  {:display "grid"
   :min-height "30rem"
   :width "45rem"})

(defn ^:export navigation-confirmation-modal []
  [navigation-confirmation-modal-s
   [modal-common/modal-with-header {:title "Are sure you want to leave the survey?"
                                    :on-cancel #(>evt [:profile/cancel-leave-survey])}
    [:div
     [:div {:style {:text-align "center" :color vars/gray}} "Your Risk Profile will not be saved unless you complete it."]
     [form/button-row-s
      [form/secondary-button-s {:on-click #(rf/>evt [:profile/cancel-leave-survey])} "Cancel"]
      [form/button {:on-click #(rf/>evt [:profile/leave-survey])} "Yes, leave survey"]]]]])

(defn profile []
  (let [personal (rf/subscribe [:personal-information])
        risk' (rf/subscribe [:risk-profile/most-recent])
        income-details (rf/subscribe [:income])
        total-expenses (rf/subscribe [:profile/total-expenses])
        error (rf/subscribe [:profile/fetch-error])
        ctx (rf/subscribe [:profile/context])
        risk-progress (r/atom 0)]
    (fn []
      (condp = @ctx
        :edit-personal-information
        [profile-grid
         [information/personal-information-form @personal]]

        :edit-insurance
        [profile-grid
         [common/form-subtitle-s "My insurance"]
         [insurance/manage-insurance {:on-save #(rf/>evt [:profile/update-insurance %])
                                      :on-cancel #(rf/>evt [:profile/navigate-to-context :view-profile])}]]

        :edit-risk-survey
        [profile-grid
         [risk/progress @risk-progress]
         [risk/risk-survey-form @risk' {:on-submit #(rf/>evt [:profile/update-risk-survey %])
                                        :on-cancel #(rf/>evt [:profile/cancel-edit-risk-survey])
                                        :on-step-change #(reset! risk-progress %)}]]

        :edit-income
        [profile-grid
         [common/form-subtitle-s "My Finances"]
         [income/income-form @income-details {:on-save #(rf/>evt [:profile/update-income %])
                                              :on-cancel #(rf/>evt [:profile/navigate-to-context :view-profile])}]]

        :manage-priorities
        [profile-grid
         [common/form-subtitle-s "My priorities & goals"]
         [priorities/manage-priorities {:on-save #(rf/>evt [:profile/update-priorities %])
                                        :on-cancel #(rf/>evt [:profile/navigate-to-context :view-profile])}]]

        :edit-assets-and-liabilities
        [profile-grid
         [common/form-subtitle-s "My finances"]
         [assets-liabilities/manage-assets-liabilities {:on-save #(rf/>evt [:profile/update-assets-and-liabilities %])
                                                        :on-cancel #(rf/>evt [:profile/navigate-to-context :view-profile])}]]

        :edit-estate-and-tax
        [profile-grid
         [common/form-subtitle-s "My finances"]
         [estate/manage {:on-save #(rf/>evt [:profile/update-estate-and-tax %])
                         :on-cancel #(rf/>evt [:profile/navigate-to-context :view-profile])}]]

        :edit-expenses
        [profile-grid
         [common/form-subtitle-s "My finances"]
         [expenses/manage {:on-cancel #(rf/>evt [:profile/navigate-to-context :view-profile])
                           :on-save #(rf/>evt [:profile/update-expenses %])}]]

        :view-profile
        [profile-grid-s {:data-test "my-profile-page"}
         [profile-s
          [page-title-s "My profile"]
          [layout-s
           [information/personal-information @personal @error]
           [priorities/financial-priorities {:on-edit #(rf/>evt [:profile/edit-priorities])}]
           [risk-profile @risk']
           [assets-liabilities/assets-liabilities {:on-edit #(rf/>evt [:profile/navigate-to-context :edit-assets-and-liabilities])}]
           [income/income @income-details {:on-edit #(rf/>evt [:profile/navigate-to-context :edit-income])}]
           [expenses/summary @total-expenses {:on-edit #(rf/>evt [:profile/navigate-to-context :edit-expenses])}]
           [insurance/insurance {:on-edit #(rf/>evt [:profile/navigate-to-context :edit-insurance])}]
           [estate/estate-and-tax {:on-edit #(rf/>evt [:profile/navigate-to-context :edit-estate-and-tax])}]]]]

        [common/loading-s
         [common/loading-body-s
          [typo/h1 "Loading Profile..."]]]))))
